// @flow

import type {ConversationDispatch} from './reducer';
import type {Edge, AnswerId} from 'src/types/chatbot';
import type {RenderNode} from './conversation-builder.jsx';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import invariant from 'invariant';

import {entries} from 'src/utils/object';
import classify from 'src/utils/classify';
import {
  getConditionOperatorLabels,
  isBranchableNodeType,
} from 'src/components/conversation-builder/utils';

import {Menu} from 'src/components/lib/token-list-input/borderless.jsx';
import Popover from './popover.jsx';
import {Button, UnstyledButton} from 'src/components/lib/new-button';
import {DescriptorText} from './common.jsx';
import {ButtonsSmall} from './form.jsx';

import css from './condition-selector.css';


export default function ConditionSelector({
  dispatch,
  content,
  edge,
}: {
  dispatch: ConversationDispatch,
  content: RenderNode[],
  edge: Edge,
}): React.Node {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const parentRenderNode = content.find(
    (renderNode) => renderNode.node?.id === edge.from_node_id,
  );

  invariant(
    parentRenderNode && parentRenderNode.branches,
    'Cannot render a condition selector for a non-existent node.',
  );

  // $FlowFixMe[incompatible-type-arg]
  const usedConditions: Set<AnswerId> = new Set(
    // $FlowIssue `flat` returns mixed
    parentRenderNode.branches
      .filter((branch) => branch.edge.id !== edge.id)
      .map(({edge}) => edge.conditions)
      .flat(),
  );

  const [selectedConditions, setConditions] = React.useState(
    () => new Set(edge.conditions),
  );

  const [operator, setOperator] = React.useState(edge.operator);

  const {node} = parentRenderNode;

  invariant(
    node && isBranchableNodeType(node),
    'Parent node must allow branching.',
  );

  const answers = node?.answers ? node.answers : [];
  let displayedAnswers = answers.filter((answer) => !answer.disabled);
  if (!node?.question_is_multiselect) {
    displayedAnswers = displayedAnswers.filter(
      (answer) => !usedConditions.has(answer.id),
    );
  }

  const handleClose = () => dispatch({type: 'CANCEL_EDIT_EDGE'});

  const conditionOptions = entries(getConditionOperatorLabels(t)).map(
    ([value, label]) => ({value, label: label.toLowerCase()}),
  );

  return (
    <Popover
      onClose={handleClose}
      titleText={t('Select_responses', 'Select responses')}
    >
      <div className={css.body}>
        <DescriptorText className={css.subtitle}>
          {node?.question_is_multiselect ? (
            <>
              <span>{t('Response', 'Response')}</span>{' '}
              {false ? (
                <Menu
                  classNames={{container: css.operatorMenu}}
                  value={operator || 'every'}
                  options={conditionOptions}
                  onChange={(option) => setOperator(option.value)}
                />
              ) : (
                <span>{t('contains', 'contains')}</span>
              )}
            </>
          ) : (
            <>
              {`${t('Select_from', 'Select from')} `}
              <strong>{answers.length}</strong>
              {` ${t('accepted_answers', 'accepted answers')}:`}
            </>
          )}
        </DescriptorText>

        <div className={css.conditions}>
          {displayedAnswers.map((answer) => (
            <ConditionToken
              key={answer.id}
              isSelected={selectedConditions.has(answer.id)}
              onClick={() => {
                const nextConditions = new Set(selectedConditions);
                if (selectedConditions.has(answer.id)) {
                  nextConditions.delete(answer.id);
                } else {
                  nextConditions.add(answer.id);
                }
                setConditions(nextConditions);
              }}
            >
              {answer.label}
            </ConditionToken>
          ))}
        </div>

        <ButtonsSmall className={css.buttons}>
          <Button size="medium" onClick={handleClose}>
            {t('Cancel', 'Cancel')}
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={() =>
              dispatch({
                type: 'SUBMIT_EDIT_EDGE',
                payload: {
                  conditions: [...selectedConditions],
                  operator,
                },
              })
            }
          >
            {t('Done', 'Done')}
          </Button>
        </ButtonsSmall>
      </div>
    </Popover>
  );
}

function ConditionToken({isSelected, ...props}) {
  return (
    <UnstyledButton
      className={classify(css.token, isSelected && css.selected)}
      {...props}
    />
  );
}
